import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import {Button, Box, Card, CardContent, Grid2, Paper, Stack, Typography, useTheme, Container, Chip, Divider, IconButton} from "@mui/material";
import {useEffect, useState} from "react";
import {useQuery, useMutation} from "@tanstack/react-query";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import {Link} from "react-router-dom";
import {urls} from "@routes";
import {CustomCellRendererProps} from "ag-grid-react";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {format} from "date-fns";
import {useNavigate, useParams} from "react-router-dom";
import {ArrowBack} from "@mui/icons-material";
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import {useSnackbar} from "notistack";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

interface CreditHistoryData {
    uid: string | null
    campaign_schedule_uid: string
    campaign_name: string
    // Set on emails sent through the Deliveryman API or replied from the Master Inbox, which have no campaign link.
    source?: "api" | "master_inbox"
    receiver: string
    sender: string
    time_of_email_sent: number | null
    status: string
}

interface PaginationData {
    email: string;
    data: CreditHistoryData[];
    total_count: number;
    total_pages: number;
    current_page: number;
    page_size: number;
}

export default function AdminSendingHistory() {
    const {userId} = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const {enqueueSnackbar} = useSnackbar();

    const [pageData, setPageData] = useState<PaginationData>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [search, setSearch] = useState("");
    const [email, setEmail] = useState("")
    const [exportLoading, setExportLoading] = useState(false);

    // Page Title
    useEffect(() => {
        document.title = "Admin Sending History - Deliveryman.ai";
    }, []);

    // Fetch the page data
    const pageDataQuery = useQuery({
        queryKey: ["SendingHistoryQuery", userId, page, pageSize, search],
        queryFn: () =>
            authenticateAndFetchData(
                `/admin/credit-history/?user_id=${userId}&page=${page}&page_size=${pageSize}&search=${search}`
            ),
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    useEffect(() => {
        if (pageDataQuery.data?.data) {
            const resp = pageDataQuery.data.data;
            setPageData({
                email: resp.email,
                data: resp.data || [],
                total_count: resp.total_count || 0,
                total_pages: resp.total_pages || 0,
                current_page: resp.current_page || 1,
                page_size: resp.page_size || 50,
            });            
        }
    }, [pageDataQuery.data]);

    const handlePageChange = (newPage: number) => setPage(newPage);
    
    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const StatusBadgeCell = (params: CustomCellRendererProps) => {
        const value = params.value;

        const badgeProps = {
            size: "small" as const,
            sx: { fontSize: "0.75rem", height: 24 },
        };

        switch (value) {
            case "Success":
                return <Chip {...badgeProps} color="success" label="Success" />;
            case "Queued":
                return <Chip {...badgeProps} color="info" label="Queued" />;
            case "Positive":
                return <Chip {...badgeProps} color="primary" label="Positive" />;
            case "Negative":
            case "Failed":
                return <Chip {...badgeProps} color="error" label={value} />;
            case "admin_test_email_failed":
                return <Chip {...badgeProps} color="error" label="Test Admin Email Falied" />;
            case "test_email_failed":
                return <Chip {...badgeProps} color="error" label="Test Email Falied" />;
            case "Bounced":
                return <Chip {...badgeProps} color="warning" label="Bounced" />;
            case "Unsubscribed":
                return <Chip {...badgeProps} color="secondary" label="Unsubscribed" />;
            case "Resubscribe":
                return <Chip {...badgeProps} sx={{backgroundColor: "#20c997", color: "white"}} label="Resubscribe" />;
            case "Neutral":
                return <Chip {...badgeProps} color="default" label="Neutral" />;
            case "Replied":
                return <Chip {...badgeProps} color="info" label="Replied" />;
            case "admin_test_pending":
                return <Chip {...badgeProps} color="info" label="Admin Test Email Pending" />;
            case "test_email_pending":
                return <Chip {...badgeProps} color="info" label="Test Email Pending" />;
            case "admin_test_email":
                return <Chip {...badgeProps} color="primary" label="Admin Test Email" />;
            case "test_email":
                return <Chip {...badgeProps} color="primary" label="Test Email" />;
            case "manual_sent":
                return <Chip {...badgeProps} color="primary" label="Manual Sent" />;
            case "":
                return <Chip {...badgeProps} color="error" label="N/A" />;
            default:
                return <Chip {...badgeProps} color="default" label={value || "Unknown"} />;
        }
    };

    function timestampFormatter(params: ValueFormatterParams) {
		if (params.value !== null) {
			return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
		} else {
			return "---"
		}
	}
    
    const CampaignDetailsLink = (cellProps: CustomCellRendererProps) => {
        if (cellProps.data?.source) {
            return (
                <span style={{ color: theme.palette.text.primary }}>
                    {cellProps.value}
                </span>
            );
        }

        if (!cellProps.value) {
            return (
                <span style={{ color: theme.palette.text.primary }}>
                    Admin Test Email
                </span>
            );
        }

        return (
            <Link
                to={urls["adminUserCampaignDetails"]
                    .replace(":userId", userId || "")
                    .replace(":campaignUID", cellProps.data["uid"])}
                state={{ from: "SendingHistory" }}
                style={{ color: theme.palette.text.primary }}
            >
                {cellProps.value}
            </Link>
        );
    };
    
    const columnCustomizerLabel = {
		campaign_name: "Campaign Name",
		receiver: "Receiver",
		sender: "Sender",
		time_of_email_sent: "Email Sent On",
		status: "Status",				
	};

    const columnDefs: ColDef[] = [
        {field: "campaign_name", headerName: "Campaign Name", cellRenderer: CampaignDetailsLink, flex: 2},
        {field: "receiver", headerName: "Receiver"},
        {field: "sender", headerName: "Sender"},
        {field: "time_of_email_sent", headerName: "Email Sent On", valueFormatter: timestampFormatter, comparator: (valueA, valueB, nodeA, nodeB) => {
            const value1 = nodeA.data["time_of_email_sent"] !== null 
            ? nodeA.data["time_of_email_sent"] 
            : 0;
            const value2 = nodeB.data["time_of_email_sent"] !== null 
            ? nodeB.data["time_of_email_sent"] 
            : 0;
            if (value1 === value2) return 0;
            return value1 > value2 ? 1 : -1;
        },
        flex: 2},            
        {field: "status", headerName: "Status", cellRenderer: StatusBadgeCell, sortable: false},            
    ]

    const creditHistoryColumns: CSVColumn<any>[] = [
        { 
            header: "Campaign Name", 
            accessor: (row) => row.campaign_name || "N/A" 
        },
        { 
            header: "Campaign UID", 
            accessor: (row) => row.uid || "N/A" 
        },
        { 
            header: "Sender Email", 
            accessor: (row) => row.sender || "N/A" 
        },
        { 
            header: "Receiver Email", 
            accessor: (row) => row.receiver || "N/A" 
        },
        { 
            header: "Status", 
            accessor: (row) => row.status || "N/A" 
        },
        { 
            header: "Type", 
            accessor: (row) => {
                if (row.is_test) return "Test Email";
                if (row.source === "api") return "Via API";
                if (row.source === "master_inbox") return "Via Master Inbox";
                return "User Campaign";
            }
        },
        {
            header: "Credit Charged",
            accessor: (row) => {
                // Master Inbox replies are free, and API emails are charged once Postal accepts them.
                if (row.is_test || row.source === "master_inbox") return "No";
                if (row.source === "api" && row.status === "Scheduled") return "No";
                return "Yes";
            }
        },
        { 
            header: "Failure Reason", 
            accessor: (row) => row.failure_reason || "N/A" 
        },
        { 
            header: "Sent At", 
            accessor: (row) => {
                const timestamp = row.time_of_email_sent || row.sent_at || row.sent_on;
                return timestamp ? new Date(Number(timestamp)).toLocaleString() : "N/A";
            } 
        },
    ];

    const ExportUserCreditHistoryCsvButton = () => {        

        const handleExportCreditHistory = async () => {
            setExportLoading(true);

            try {
                const response = await authenticateAndFetchData(
                    `/admin/credit-history/?user_id=${userId}&export_all=true`
                );
                
                const recordsToExport = response?.data?.data || [];

                if (!recordsToExport.length) {
                    enqueueSnackbar("No credit history data available to export.", { variant: "error" });
                    return;
                }

                const success = exportToCSV(
                    recordsToExport,
                    creditHistoryColumns,                    
                    getExportFilename(user?.email ?? "unknown", "SendingHistory")
                );

                if (success) {
                    enqueueSnackbar("Credit history exported successfully!", { variant: "success" });
                } else {
                    enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
                }
            } catch (err) {
                console.error("Export error:", err);
                enqueueSnackbar("Error while exporting credit history.", { variant: "error" });
            } finally {
                setExportLoading(false);
            }
        };

        return (
            <Button
                startIcon={<Downloading />}
                variant="contained"
                color="primary"
                onClick={handleExportCreditHistory}
                disabled={exportLoading}
            >
                {exportLoading ? "Exporting..." : "Export"}
            </Button>
        );
    };

    if (!pageData && pageDataQuery.isLoading) {
        return (
            <PageLoading/>
        )

    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

    } else {
        return(
            <Stack direction={"column"} spacing={4} sx={{pb: 4, height: "100%"}}>
                            <Stack direction={"row"} spacing={2} alignItems={"center"}>
                                <IconButton onClick={() => navigate(urls["adminUserDetails"].replace(":userId", userId || ""))}><ArrowBack/></IconButton>
                                <Typography fontWeight={"bold"} variant={"h5"}>Sending History ({pageData?.email || "..."})</Typography>
                            </Stack>
                            <Divider/>                            
                            <Box sx={{ height: "600px", width: "100%", overflow: "hidden" }}>
                                <PaginatedServerSideDataTable
                                    columns={columnDefs}
                                    paginationData={pageData}
                                    loading={pageDataQuery.isFetching}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handlePageSizeChange}
                                    onSearchChange={handleSearchChange}
                                    searchPlaceholder="Search campaign, receiver, sender or status..."
                                    columnLabels={columnCustomizerLabel}
                                    noRowsText={"No Credit History Available"}
                                    actions={<ExportUserCreditHistoryCsvButton />}
                                />
                            </Box>                
                        </Stack>
        )
    }
}
